import { DisplayMoldOrderStatus, FormFieldConfig, Mold, MoldOrderFirstLevelType, MoldOrderMainTypeStruct, MoldOrderStatus, MoldOrderType } from '../types/mold';
import skConfig from './companyConfig/sk';
import sbiConfig from './companyConfig/sbi';
import { GenericColumn } from '@/components/KanbanBoard/utils/tableColumns';

export type ClientCompanyCode = 'sk' | 'sbi';

/**
 * Available mold display field types for client-specific configurations
 * Constrained to actual Mold properties for type safety
 */
export type MoldDisplayFieldType = keyof Mold;

export interface FeatureToggles {
    enableRepairMoldOrder: boolean;
    enableMajorRepair: boolean;
    enabledModifyOrder: boolean;
    enableMoldAcceptanceCreation: boolean;
    /** When creating a new mold, always set its initial status to "ready" on the New Mold Creation page. */
    enableNewMoldWithReadyStatus: boolean;
    /** Show the button to generate pdf in mold detail page */
    enabledGeneratePdfButton: boolean;
    enableMultiApprovers: boolean;
    /** Enable the Shot Count Log Page in the navigation menu */
    enableShotCountLogPage: boolean;
    /** Show mold guide pages on the mold detail page. */
    enableMoldGuides: boolean;
    /**
     * Gates VISIBILITY of the v2 (server-paginated) mold order list entry point only
     * (mold-order-list-v2 plan, decision #5). The v1 list page (`/mold-orders`) and its
     * "Try new list" cross-link are never disabled by this toggle being false — only the
     * cross-link is hidden. The `/mold-orders/v2` route itself always stays reachable by
     * direct URL for QA regardless of this flag.
     */
    enableServerSideMoldOrderList: boolean;
}

/**
 * Column configuration for table views - centralized in company configs
 */
export interface SimpleColumnConfig {
    /** All column keys available for this customer (in order) - shows in ColumnSelector */
    availableColumns: string[];
    /** Columns visible by default (subset of availableColumns) */
    defaultVisibleColumns: string[];
    /** Custom labels for specific columns (optional) */
    customLabels?: Record<string, string>;
}

/**
 * Individual search field configuration
 */
export interface SearchFieldConfig {
    /** Unique identifier for the search field (maps to column or special field) */
    id: string;
    /** Display label for the search field */
    label: string;
    /** Placeholder text for the search input */
    placeholder: string;
    /** Whether this search field is available for this company */
    enabled?: boolean;
}

/**
 * Search configuration for different entities - centralized in company configs  
 */
export interface SearchConfig {
    /** Available search fields for this customer (in order) */
    availableSearchFields: SearchFieldConfig[];
    /** Default search field to be selected */
    defaultSearchField?: string;
}

export interface IconProps {
    color?: string;
    activeColor?: string;
    size?: number;
    strokeWidth?: number;
    showHolders?: boolean;
}

export interface IconConfig {
    name: string;
    source: string;
    props?: IconProps;
    disabledProps?: IconProps;
}

export interface UIConfig {
    icon: IconConfig;
    header: {
        bgColor: string;
        textColor: string;
        borderColor: string;
        shadow: string;
    },
    colorCode: string;
}

export type StatusMappingRecord = Record<string, string>
export type StatusMappingKey = 'all' | number;

export type ActionButtonType = 'ViewQuestionnaire' | 'FillQuestionnaire' | 'PrioritizeMoldOrder' | 'PrintQRCode';
export interface ActionButtonMapping {
    buttonType: 'submit' | 'approval';
    customAcceptButtonLabel?: string;
    customRejectButtonLabel?: string;
    buttons: ActionButtonType[];
}

export interface NextStepActionConfig {
    formConfigs: FormFieldConfig[];
}

export interface StatusResponseReminder {
    /** Escalate the count to amber once days-in-status reaches this threshold. */
    warnAfterDays: number;
    /** Label template; {days} is replaced with the elapsed day count. */
    label?: string;
}

export interface StatusDueDateBehavior {
    /** Hide the default due-date delay/overdue warning while in this status. */
    suppressDueDateWarning?: boolean;
    /** Show a neutral "time in this status" count, escalating to amber at the threshold. */
    responseReminder?: StatusResponseReminder;
}

export interface MoldOrderCreationConfig {
    /** Allowed mold statuses for creating this order type */
    allowedStatuses: string[];
    /** Feature toggle key that must be enabled for this order type */
    featureToggle?: keyof FeatureToggles;
    /** Custom message to show when order creation is disabled due to feature toggle */
    customDisabledMessage?: string;
}

export interface MoldOrderFormConfig {
    title: string;
    orderTemplateVersion: string;
    formConfigs: FormFieldConfig[];
    /**
     * If true, the form will be checked for completion before allowing the next step.
     * This is to ensure all required fields are filled.
     * Default is false, meaning the form can be submitted without checking for completion.
     */
    checkForFormCompletion?: boolean;
    moldOrderMainTypes: MoldOrderMainTypeStruct[];
    moldOrderTypes: MoldOrderType[];
    isEnable: string | boolean;
    uiConfig?: UIConfig;
    statusMapping: Record<StatusMappingKey, StatusMappingRecord>;
    disallowSetNextAssignee?: Record<StatusMappingKey, MoldOrderStatus[]>;
    colorCodeMapping?: Record<StatusMappingKey, Record<string, string>>;
    actionButtonMapping?: Record<string, ActionButtonMapping>;
    iconStatusMapping: Record<StatusMappingKey, StatusMappingRecord>;
    /**
     * Configuration for additional form fields that appear at specific statuses
     * Allows users to update specific order details before proceeding to next step
     * Example: updating exportDate at PENDING_EXPORT status
     */
    nextStepAction?: Record<string, NextStepActionConfig>;
    /** Configuration for order creation validation rules */
    creationConfig?: MoldOrderCreationConfig;
    /**
     * Per-status overrides for the due-date indicator, used when the order is
     * waiting on an external party (e.g. customer review). Keyed by workflow
     * version with 'all' as fallback, mirroring statusMapping.
     */
    statusDueDateBehavior?: Record<StatusMappingKey, Partial<Record<MoldOrderStatus, StatusDueDateBehavior>>>;
}

export interface ExtendedMoldOrderFormConfig extends MoldOrderFormConfig {
    key: string;
    isEnabled: boolean;
}

// Fields to display in the mold detail view
export interface DisplayFieldsGroup {
    key: string;
    label: string;
    fields: any[];
    uiConfig: {
        icon: string;
        showShotCountBar: boolean;
    }
}

export interface MoldDisplayFieldConfig {
    /** Primary mold field (typically ID field) */
    primaryField: MoldDisplayFieldType;
    /** Secondary mold field */
    secondaryField: MoldDisplayFieldType;
}

export interface BoardColumn {
    id?: string; // unique identifier
    title: string;
    statusIds: MoldOrderStatus[];
    colorCode?: string; // 'blue', 'green', 'yellow', 'gray', 'amber', etc.
    color?: string; // Tailwind classes like 'bg-blue-100 text-blue-800'
    textColor?: string; // Tailwind classes like 'text-blue-800'
    hoverColor?: string; // Tailwind classes like 'hover:bg-blue-200'
    countBg?: string; // Tailwind classes like 'bg-blue-50'
    icon?: string; // LucideIcon or similar
}

export interface MoldOrderTypeGroup {
    value: string;
    label: string;
    subTypes: MoldOrderType[];
}

export interface BoardConfig {
    key: string;
    group: MoldOrderFirstLevelType;
    moldOrderTypes: MoldOrderType[];
    filters: {
        moldOrderTypeGroups: MoldOrderTypeGroup[]
    }
    version?: number;
    title: string;
    columns: BoardColumn[];
    isEnable: boolean;
    uiConfig: UIConfig;
    createNewOrderUrl: string | null;
    relatedBoardKey?: string;
}

/**
 * Configuration for work order cards displayed on the homepage
 */
export interface WorkOrderCardConfig {
    /** Unique identifier for this card - should match a mold order group key */
    id: string;
    /** Title text displayed on the card */
    title: string;
    /** Subtitle text displayed on the card (e.g., "กำลังดำเนินการ") */
    subtitle: string;
    /** Subtitle text when the card is disabled */
    disabledSubtitle?: string;
    /** Whether to show this card on the homepage */
    show: boolean;
    /** Route to navigate to when the card is clicked */
    route?: string;
}

/**
 * Individual task tab configuration item (used in 'defined' mode)
 */
export interface TaskTabItem {
    /** Unique identifier for this tab */
    id: string;
    /** Display label for the tab */
    label: string;
    /** Whether to show this tab in MyTaskSection */
    show: boolean;
    /** Specific mold order types to filter by */
    moldOrderTypes: MoldOrderType[];
}

/**
 * Task tabs configuration - supports two modes:
 * - 'computed': Automatically generate tabs from moldOrderGroups
 * - 'defined': Use explicitly defined tabs from configuration
 */
export type TaskTabsConfig =
    | {
        /** Generate tabs automatically from moldOrderGroups */
        type: 'computed';
    }
    | {
        /** Use explicitly defined tabs */
        type: 'defined';
        /** Array of tab configurations to display */
        tabs: TaskTabItem[];
    };

/**
 * Homepage configuration for client-specific settings
 */
export interface HomepageConfig {
    /** Configuration for work order cards displayed on the homepage */
    workOrderCards: WorkOrderCardConfig[];
    /** Configuration for task tabs in MyTaskSection */
    taskTabs: TaskTabsConfig;
}

export interface AppState {
    appName: string;
    featureToggles: {
        production: FeatureToggles;
        staging: FeatureToggles;
        dev: FeatureToggles;
    };
    moldOrders: Record<string, MoldOrderFormConfig>;
    mold: {
        statusLabelMapping: Record<string, string>;
        /** Form configuration for mold-related forms */
        formConfiguration: Record<string, FormFieldConfig[]>;
        displayFieldGroups: DisplayFieldsGroup[];
        /** Client-specific mold display fields configuration */
        displayFields: MoldDisplayFieldConfig;
    };
    moldOrderDetail: {
        header: {
            title: string;
            subtitle: string;
        }
    };
    moldOrderCreation: {
        moldTable: {
            visibleColumns: GenericColumn<Mold>[];
        },
        orderTypes: string[],
    }
    kanban: {
        card: {
            /** Mold field shown as title in card */
            title: string;
            /** Mold field shown as subtitle in card */
            subtitle: string;
        };
        boards: {
            data: BoardConfig[];
            defaultBoardKey: string;
        }
    };
    dashboard: {
        taskRow: {
            /** Mold field shown in the info section of TaskRow */
            infoField: string;
        }
        /** Shift boundary (local hour, 0–23): day shift A = [dayStartHour,
         *  dayEndHour); B is the rest. Used by the technician shift report;
         *  the user can override it in the UI. Defaults to 08:00–20:00. */
        shifts?: {
            dayStartHour: number;
            dayEndHour: number;
        };
    };
    table: {
        /** Column configuration for mold orders table */
        moldOrdersColumns: SimpleColumnConfig;
        /** Column configuration for mold orders table in mold detail page */
        moldOrdersInMoldPageColumns: SimpleColumnConfig;
        /** Column configuration for molds table */
        moldColumns: SimpleColumnConfig;
    };
    search: {
        /** Search field configuration for molds */
        moldSearchFields: SearchConfig;
        /** Search field configuration for mold orders */
        moldOrderSearchFields: SearchConfig;
    };
    /** Homepage configuration */
    homepage: HomepageConfig;
}

export const data: Record<ClientCompanyCode, AppState> = {
    'sk': skConfig as AppState,
    'sbi': sbiConfig,
}

export const getAppState = (companyCode: ClientCompanyCode): AppState => {
    return data[companyCode];
}
